import {
  TextInput,
  Textarea,
  Stack,
  Switch,
  Box,
  Text,
  Loader,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useCallback, useEffect, useRef, useState } from "react";
import { IconUpload, IconPhoto, IconAlertCircle } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import {
  Facility,
  facilitiesService,
} from "../../../services/admin/facilities.service";
import { required, scrollToFirstError } from "../../../utils/formValidation";

export interface FacilityFormValues {
  name: string;
  address: string;
  phone: string;
  fax: string;
  is_active: boolean;
  logoS3Key?: string;
}

interface FacilityFormProps {
  initialValues?: Facility;
  onSubmit: (values: FacilityFormValues) => void;
  isLoading?: boolean;
}

const ACCEPTED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/svg+xml",
];

export default function FacilityForm({
  initialValues,
  onSubmit,
  isLoading,
}: FacilityFormProps) {
  const form = useForm<FacilityFormValues>({
    initialValues: {
      name: initialValues?.name || "",
      address: initialValues?.address || "",
      phone: initialValues?.phone || "",
      fax: initialValues?.fax || "",
      is_active: initialValues?.is_active ?? true,
      logoS3Key:
        initialValues?.logo_s3_key || initialValues?.logoS3Key || undefined,
    },
    validate: {
      name: required("Facility name is required"),
      address: required("Address is required"),
    },
  });

  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoUploadError, setLogoUploadError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  // True when the preview URL is a local blob that needs revoking on cleanup
  const isLocalPreviewRef = useRef(false);
  const previewObjectUrlRef = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const revokePreviewUrl = useCallback(() => {
    if (isLocalPreviewRef.current && previewObjectUrlRef.current) {
      URL.revokeObjectURL(previewObjectUrlRef.current);
    }
    previewObjectUrlRef.current = null;
    isLocalPreviewRef.current = false;
  }, []);

  // Reset form and logo state when editing a different facility
  useEffect(() => {
    const key = initialValues?.logo_s3_key || initialValues?.logoS3Key || null;
    form.setValues({
      name: initialValues?.name || "",
      address: initialValues?.address || "",
      phone: initialValues?.phone || "",
      fax: initialValues?.fax || "",
      is_active: initialValues?.is_active ?? true,
      logoS3Key: key || undefined,
    });
    form.clearErrors();
    revokePreviewUrl();
    setLogoPreviewUrl(null);
    setLogoUploadError(null);

    // Load existing logo preview
    if (key) {
      facilitiesService
        .getLogoDownloadUrl(key)
        .then((url) => {
          setLogoPreviewUrl(url);
          previewObjectUrlRef.current = url;
          isLocalPreviewRef.current = false;
        })
        .catch(() => {
          // Non-fatal — preview stays empty
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues?.id]);

  // Revoke local blob URLs on unmount
  useEffect(() => () => revokePreviewUrl(), [revokePreviewUrl]);

  const uploadFile = async (file: File) => {
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      notifications.show({
        title: "Invalid file type",
        message: "Please upload a PNG, JPG, GIF, WebP, or SVG image.",
        color: "red",
      });
      return;
    }

    // Show local blob preview immediately
    revokePreviewUrl();
    const objectUrl = URL.createObjectURL(file);
    previewObjectUrlRef.current = objectUrl;
    isLocalPreviewRef.current = true;
    setLogoPreviewUrl(objectUrl);
    setLogoUploadError(null);
    form.setFieldValue("logoS3Key", undefined);

    // Auto-upload to S3
    setLogoUploading(true);
    try {
      const { url, key } = await facilitiesService.getLogoPresignedUrl(
        file.name,
        file.type
      );
      await facilitiesService.uploadLogoToS3(url, file);
      form.setFieldValue("logoS3Key", key);
    } catch {
      setLogoUploadError("Upload failed. Please try again.");
      notifications.show({
        title: "Upload failed",
        message: "Could not upload the logo. Please try again.",
        color: "red",
      });
    } finally {
      setLogoUploading(false);
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget === e.target) setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) uploadFile(file);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
    e.target.value = "";
  };

  const handleSubmit = form.onSubmit(
    (values) => onSubmit(values),
    () => scrollToFirstError(form)
  );

  return (
    <form noValidate onSubmit={handleSubmit}>
      <Stack gap="md">
        <TextInput
          label="Facility Name"
          placeholder="Enter facility name"
          withAsterisk
          disabled={isLoading}
          {...form.getInputProps("name")}
        />

        <Textarea
          label="Address"
          placeholder="Enter facility address"
          withAsterisk
          minRows={3}
          disabled={isLoading}
          {...form.getInputProps("address")}
        />

        <TextInput
          label="Phone"
          placeholder="e.g. (713) 555-1234"
          disabled={isLoading}
          {...form.getInputProps("phone")}
        />

        <TextInput
          label="Fax"
          placeholder="e.g. (713) 555-5678"
          disabled={isLoading}
          {...form.getInputProps("fax")}
        />

        {/* Logo upload — only shown when editing an existing facility */}
        {initialValues?.id && (
          <Box>
            <Text size="sm" fw={500} mb={6}>
              Facility Logo
            </Text>

            {/* Hidden native file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_IMAGE_TYPES.join(",")}
              style={{ display: "none" }}
              onChange={handleFileInputChange}
            />

            {/* Current logo preview — shown when a logo exists */}
            {logoPreviewUrl && (
              <Box style={{ marginBottom: 8 }}>
                <img
                  src={logoPreviewUrl}
                  alt="Current logo"
                  style={{
                    maxHeight: 48,
                    maxWidth: 140,
                    objectFit: "contain",
                    display: "block",
                  }}
                />
                {logoUploading && (
                  <Box
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      marginTop: 4,
                    }}
                  >
                    <Loader size={12} />
                    <Text size="xs" c="dimmed">
                      Uploading…
                    </Text>
                  </Box>
                )}
                {!logoUploading && logoUploadError && (
                  <Box
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      marginTop: 4,
                    }}
                  >
                    <IconAlertCircle
                      size={13}
                      color="var(--mantine-color-red-6)"
                    />
                    <Text size="xs" c="red">
                      {logoUploadError}
                    </Text>
                  </Box>
                )}
              </Box>
            )}

            {/* Drag-and-drop zone — always visible */}
            <Box
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              style={{
                position: "relative",
                borderRadius: 8,
                border: isDragging
                  ? "2px dashed var(--mantine-color-blue-5)"
                  : "2px dashed var(--mantine-color-gray-4)",
                backgroundColor: isDragging
                  ? "var(--mantine-color-blue-0)"
                  : "var(--mantine-color-gray-0)",
                transition: "all 0.2s ease",
                padding: 16,
                cursor: "pointer",
              }}
            >
              {/* Drag overlay */}
              {isDragging && (
                <Box
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "rgba(255,255,255,0.9)",
                    borderRadius: 6,
                    zIndex: 10,
                  }}
                >
                  <IconUpload size={24} color="var(--mantine-color-blue-5)" />
                  <Text size="sm" c="blue" fw={500} mt={4}>
                    Drop image here
                  </Text>
                </Box>
              )}

              <Box
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: 64,
                }}
              >
                <IconPhoto size={22} color="var(--mantine-color-gray-5)" />
                <Text size="sm" c="dimmed" fw={500} mt={4} ta="center">
                  {logoPreviewUrl
                    ? "Drag & drop to replace, or click to select"
                    : "Drag & drop logo here, or click to select"}
                </Text>
                <Text size="xs" c="dimmed" mt={2}>
                  PNG, JPG, SVG, WebP
                </Text>
              </Box>
            </Box>

            <Text size="xs" c="dimmed" mt={4}>
              Displayed on the left of the report header for global accessioning
              cases.
            </Text>
          </Box>
        )}

        <Switch
          label="Active"
          description="Inactive facilities will not appear in dropdowns"
          disabled={isLoading}
          {...form.getInputProps("is_active", { type: "checkbox" })}
        />
      </Stack>
    </form>
  );
}

export { useForm } from "@mantine/form";
